import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, FileText, Mail, Download, Eye, User, Linkedin } from 'lucide-react';
import { WordOnlineViewer } from '@/components/WordOnlineViewer';
import { supabase } from '@/integrations/supabase/client';

const SAMPLE_RESUME_JSON = `{
  "Name": "John Doe",
  "Phone": "(555) 123-4567",
  "Email": "john.doe@email.com",
  "City": "New York",
  "State": "NY",
  "LinkedIn": "linkedin.com/in/johndoe",
  "Summary": "Results-driven software engineer with 8+ years of experience building scalable web applications. Expert in React, Node.js, and cloud technologies.",
  "Skills": [
    { "Skill": "React" },
    { "Skill": "TypeScript" },
    { "Skill": "Node.js" },
    { "Skill": "AWS" }
  ],
  "CareerHighlights": [
    { "chtitle": "Led Migration", "chdescription": "Spearheaded migration of legacy system to microservices, reducing costs by 40%" }
  ],
  "ProfessionalExperience": [
    {
      "job_title": "Senior Software Engineer",
      "company": "Tech Corp",
      "location": "New York, NY",
      "start_date": "Jan 2020",
      "end_date": "Present",
      "Descriptions": [
        { "Description": "Led team of 5 engineers in developing customer-facing features" },
        { "Description": "Reduced API response time by 60% through optimization" }
      ]
    }
  ],
  "Education": [
    {
      "College_Degree_Name": "Bachelor of Science",
      "Major": "Computer Science",
      "College_Name": "MIT",
      "City": "Cambridge",
      "ST": "MA",
      "Year_Earned": "2016"
    }
  ],
  "CertificationsAndLicenses": [],
  "TechnicalSkills": [],
  "Languages": [],
  "AwardsAndHonors": [],
  "ProfessionalAffiliations": [],
  "Publications": [],
  "Projects": [],
  "Presentations": [],
  "Portfolio": [],
  "TrainingAndDevelopment": [],
  "MilitaryExperience": [],
  "VolunteerMemberships": []
}`;

const SAMPLE_COVER_LETTER_JSON = `{
  "Name": "John Doe",
  "Phone": "(555) 123-4567",
  "Email": "john.doe@email.com",
  "Address": "123 Main St, New York, NY 10001",
  "LinkedIn": "linkedin.com/in/johndoe",
  "CompanyName": "Acme Corporation",
  "RecipientName": "Jane Smith",
  "RecipientTitle": "Hiring Manager",
  "JobTitle": "Senior Software Engineer",
  "Body": "Dear Jane Smith,\\n\\nI am writing to express my strong interest in the Senior Software Engineer position at Acme Corporation. With over 8 years of experience in software development and a proven track record of delivering high-impact solutions, I am confident I would be a valuable addition to your team.\\n\\nIn my current role at Tech Corp, I have led the development of several key initiatives that resulted in significant cost savings and improved customer satisfaction. My expertise in React, Node.js, and cloud technologies aligns perfectly with your requirements.\\n\\nI would welcome the opportunity to discuss how my skills and experience can contribute to Acme Corporation's continued success.\\n\\nSincerely,\\nJohn Doe"
}`;

const SAMPLE_LINKEDIN_JSON = `{
  "LinkedIn_First_Name": "John",
  "LinkedIn_Last_Name": "Doe",
  "LinkedIn_Headline_1": "Senior Software Engineer | React & Node.js Expert | Building Scalable Solutions",
  "LinkedIn_Headline_2": "Passionate about transforming complex problems into elegant, user-friendly applications",
  "LinkedIn_About": "I'm a results-driven software engineer with 8+ years of experience building web applications that scale. I specialize in React, TypeScript, and Node.js, and I'm passionate about creating solutions that make a real impact.\\n\\nMy approach combines technical excellence with strong communication skills. I believe the best software comes from truly understanding user needs and translating them into intuitive experiences.\\n\\nKey areas of expertise:\\n• Full-stack development (React, Node.js, TypeScript)\\n• Cloud architecture (AWS, GCP)\\n• Team leadership and mentoring\\n• Agile methodologies\\n\\nI'm always open to connecting with fellow tech enthusiasts and exploring new opportunities. Let's chat!",
  "LinkedIn_Experience": "At Tech Corp, I lead a team of 5 engineers building customer-facing features that serve 1M+ users. Key accomplishments include:\\n\\n• Architected and implemented a microservices migration that reduced infrastructure costs by 40%\\n• Improved API response times by 60% through strategic optimization\\n• Mentored 3 junior developers who have since been promoted\\n• Introduced automated testing practices that reduced production bugs by 75%",
  "LinkedIn_URL": "linkedin.com/in/johndoe"
}`;

const SAMPLE_EXECUTIVE_BIO_JSON = `{
  "Name": "John Doe",
  "Title": "Chief Technology Officer",
  "Phone": "(555) 123-4567",
  "Email": "john.doe@email.com",
  "City": "New York",
  "State": "NY",
  "LinkedIn": "linkedin.com/in/johndoe",
  "BioParagraphs": [
    "John Doe is an accomplished technology executive with over 15 years of experience leading digital transformation initiatives for Fortune 500 companies. As Chief Technology Officer at Tech Corp, he oversees a team of 50+ engineers and is responsible for the company's technical strategy and innovation roadmap.",
    "Prior to joining Tech Corp, John held senior engineering roles at Google and Microsoft, where he led the development of products used by millions of users worldwide. He is a recognized thought leader in cloud computing and has spoken at numerous industry conferences including AWS re:Invent and Google I/O.",
    "John holds a Master's degree in Computer Science from Stanford University and a Bachelor's degree from MIT. He is passionate about mentoring the next generation of tech leaders and serves on the advisory boards of several technology startups."
  ],
  "KeyAccomplishments": [
    "Led digital transformation saving $10M annually",
    "Built and scaled engineering team from 10 to 50+ members",
    "Launched 3 products reaching 5M+ users",
    "Filed 5 patents in distributed systems"
  ],
  "Expertise": [
    "Cloud Architecture",
    "Digital Transformation",
    "Team Leadership",
    "Strategic Planning",
    "Product Development"
  ]
}`;

export default function DocmosisJsonTest() {
  const [resumeJson, setResumeJson] = useState(SAMPLE_RESUME_JSON);
  const [coverLetterJson, setCoverLetterJson] = useState(SAMPLE_COVER_LETTER_JSON);
  const [linkedinJson, setLinkedinJson] = useState(SAMPLE_LINKEDIN_JSON);
  const [executiveBioJson, setExecutiveBioJson] = useState(SAMPLE_EXECUTIVE_BIO_JSON);
  
  const [isGeneratingResume, setIsGeneratingResume] = useState(false);
  const [isGeneratingLetter, setIsGeneratingLetter] = useState(false);
  const [isGeneratingLinkedin, setIsGeneratingLinkedin] = useState(false);
  const [isGeneratingBio, setIsGeneratingBio] = useState(false);
  
  const [generatedResumeUrl, setGeneratedResumeUrl] = useState<string | null>(null);
  const [generatedLetterUrl, setGeneratedLetterUrl] = useState<string | null>(null);
  const [generatedLinkedinUrl, setGeneratedLinkedinUrl] = useState<string | null>(null);
  const [generatedBioUrl, setGeneratedBioUrl] = useState<string | null>(null);
  
  const [activeTab, setActiveTab] = useState('resume');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const validateJson = (jsonString: string): { valid: boolean; data?: any; error?: string } => {
    try {
      const data = JSON.parse(jsonString);
      return { valid: true, data };
    } catch (e) {
      return { valid: false, error: (e as Error).message };
    }
  };

  const uploadToStorage = async (blob: Blob, fileName: string): Promise<string> => {
    const { error: uploadError } = await supabase.storage
      .from('word-documents')
      .upload(fileName, blob, {
        contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        upsert: true
      });

    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage
      .from('word-documents')
      .getPublicUrl(fileName);

    return urlData.publicUrl;
  };

  const base64ToBlob = (base64: string): Blob => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { 
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
    });
  };

  const generateResume = async () => {
    const validation = validateJson(resumeJson);
    if (!validation.valid) {
      toast.error(`Invalid JSON: ${validation.error}`);
      setJsonError(validation.error || 'Invalid JSON');
      return;
    }
    setJsonError(null);

    setIsGeneratingResume(true);
    try {
      console.log('Sending resume data to Docmosis:', validation.data);

      const response = await fetch(
        'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/generate-resume',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ resumeData: validation.data })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Docmosis error: ${response.status} - ${errorText}`);
      }

      const blob = await response.blob();
      const fileName = `test-resume-${Date.now()}.docx`;
      const publicUrl = await uploadToStorage(blob, fileName);

      setGeneratedResumeUrl(publicUrl);
      toast.success('Resume generated successfully!');
    } catch (error) {
      console.error('Resume generation error:', error);
      toast.error(`Failed to generate resume: ${(error as Error).message}`);
    } finally {
      setIsGeneratingResume(false);
    }
  };

  const generateCoverLetter = async () => {
    const validation = validateJson(coverLetterJson);
    if (!validation.valid) {
      toast.error(`Invalid JSON: ${validation.error}`);
      setJsonError(validation.error || 'Invalid JSON');
      return;
    }
    setJsonError(null);

    setIsGeneratingLetter(true);
    try {
      console.log('Sending cover letter data to Docmosis:', validation.data);

      const response = await fetch(
        'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/generate-cover-letter',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ letterData: validation.data })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Docmosis error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success || !result.data) {
        throw new Error(result.error || 'No document data returned');
      }

      const blob = base64ToBlob(result.data);
      const fileName = `test-cover-letter-${Date.now()}.docx`;
      const publicUrl = await uploadToStorage(blob, fileName);

      setGeneratedLetterUrl(publicUrl);
      toast.success('Cover letter generated successfully!');
    } catch (error) {
      console.error('Cover letter generation error:', error);
      toast.error(`Failed to generate cover letter: ${(error as Error).message}`);
    } finally {
      setIsGeneratingLetter(false);
    }
  };

  const generateLinkedin = async () => {
    const validation = validateJson(linkedinJson);
    if (!validation.valid) {
      toast.error(`Invalid JSON: ${validation.error}`);
      setJsonError(validation.error || 'Invalid JSON');
      return;
    }
    setJsonError(null);

    setIsGeneratingLinkedin(true);
    try {
      console.log('Sending LinkedIn data to Docmosis:', validation.data);

      const response = await fetch(
        'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/generate-linkedin-document',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(validation.data)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Docmosis error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success || !result.files?.[0]?.data) {
        throw new Error(result.error || 'No document data returned');
      }

      const blob = base64ToBlob(result.files[0].data);
      const fileName = `test-linkedin-${Date.now()}.docx`;
      const publicUrl = await uploadToStorage(blob, fileName);

      setGeneratedLinkedinUrl(publicUrl);
      toast.success('LinkedIn document generated successfully!');
    } catch (error) {
      console.error('LinkedIn generation error:', error);
      toast.error(`Failed to generate LinkedIn document: ${(error as Error).message}`);
    } finally {
      setIsGeneratingLinkedin(false);
    }
  };

  const generateExecutiveBio = async () => {
    const validation = validateJson(executiveBioJson);
    if (!validation.valid) {
      toast.error(`Invalid JSON: ${validation.error}`);
      setJsonError(validation.error || 'Invalid JSON');
      return;
    }
    setJsonError(null);

    setIsGeneratingBio(true);
    try {
      console.log('Sending Executive Bio data to Docmosis:', validation.data);

      const response = await fetch(
        'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/generate-executive-bio',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ bioData: validation.data })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Docmosis error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (!result.success || !result.data) {
        throw new Error(result.error || 'No document data returned');
      }

      const blob = base64ToBlob(result.data);
      const fileName = `test-executive-bio-${Date.now()}.docx`;
      const publicUrl = await uploadToStorage(blob, fileName);

      setGeneratedBioUrl(publicUrl);
      toast.success('Executive Bio generated successfully!');
    } catch (error) {
      console.error('Executive Bio generation error:', error);
      toast.error(`Failed to generate Executive Bio: ${(error as Error).message}`);
    } finally {
      setIsGeneratingBio(false);
    }
  };

  const downloadDocument = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderDocumentTab = (
    type: 'resume' | 'cover-letter' | 'linkedin' | 'executive-bio',
    icon: React.ReactNode,
    title: string,
    description: string,
    jsonValue: string,
    setJsonValue: (v: string) => void,
    sampleJson: string,
    isGenerating: boolean,
    generateFn: () => void,
    generatedUrl: string | null,
    downloadName: string
  ) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {icon}
            {title} JSON Input
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>JSON Data</Label>
            <Textarea
              value={jsonValue}
              onChange={(e) => setJsonValue(e.target.value)}
              className="font-mono text-sm min-h-[400px]"
              placeholder="Paste VectorShift JSON here..."
            />
          </div>
          
          {jsonError && activeTab === type && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              JSON Error: {jsonError}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={generateFn}
              disabled={isGenerating}
              className="flex-1"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Generate & Preview
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setJsonValue(sampleJson)}
            >
              Reset Sample
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Document Preview</span>
            {generatedUrl && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadDocument(generatedUrl, downloadName)}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            )}
          </CardTitle>
          <CardDescription>Generated document will appear here</CardDescription>
        </CardHeader>
        <CardContent>
          {generatedUrl ? (
            <div className="h-[500px] border rounded-lg overflow-hidden">
              <WordOnlineViewer
                documentUrl={generatedUrl}
                fileName={downloadName}
              />
            </div>
          ) : (
            <div className="h-[500px] border rounded-lg flex items-center justify-center bg-muted/30">
              <div className="text-center text-muted-foreground">
                {icon}
                <p className="mt-4">Generate a document to see the preview</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Docmosis JSON Test</h1>
        <p className="text-muted-foreground mt-2">
          Test VectorShift JSON output against Docmosis document generation endpoints
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="resume" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Resume
          </TabsTrigger>
          <TabsTrigger value="cover-letter" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Cover Letter
          </TabsTrigger>
          <TabsTrigger value="linkedin" className="flex items-center gap-2">
            <Linkedin className="w-4 h-4" />
            LinkedIn
          </TabsTrigger>
          <TabsTrigger value="executive-bio" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Exec Bio
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resume" className="space-y-6">
          {renderDocumentTab(
            'resume',
            <FileText className="w-5 h-5" />,
            'Resume',
            'Paste your VectorShift resume JSON output here',
            resumeJson,
            setResumeJson,
            SAMPLE_RESUME_JSON,
            isGeneratingResume,
            generateResume,
            generatedResumeUrl,
            'test-resume.docx'
          )}
        </TabsContent>

        <TabsContent value="cover-letter" className="space-y-6">
          {renderDocumentTab(
            'cover-letter',
            <Mail className="w-5 h-5" />,
            'Cover Letter',
            'Paste your VectorShift cover letter JSON output here',
            coverLetterJson,
            setCoverLetterJson,
            SAMPLE_COVER_LETTER_JSON,
            isGeneratingLetter,
            generateCoverLetter,
            generatedLetterUrl,
            'test-cover-letter.docx'
          )}
        </TabsContent>

        <TabsContent value="linkedin" className="space-y-6">
          {renderDocumentTab(
            'linkedin',
            <Linkedin className="w-5 h-5" />,
            'LinkedIn Profile',
            'Paste your VectorShift LinkedIn JSON output here',
            linkedinJson,
            setLinkedinJson,
            SAMPLE_LINKEDIN_JSON,
            isGeneratingLinkedin,
            generateLinkedin,
            generatedLinkedinUrl,
            'test-linkedin.docx'
          )}
        </TabsContent>

        <TabsContent value="executive-bio" className="space-y-6">
          {renderDocumentTab(
            'executive-bio',
            <User className="w-5 h-5" />,
            'Executive Bio',
            'Paste your VectorShift Executive Bio JSON output here',
            executiveBioJson,
            setExecutiveBioJson,
            SAMPLE_EXECUTIVE_BIO_JSON,
            isGeneratingBio,
            generateExecutiveBio,
            generatedBioUrl,
            'test-executive-bio.docx'
          )}
        </TabsContent>
      </Tabs>

      {/* JSON Schema Reference */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Expected JSON Schemas</CardTitle>
          <CardDescription>
            Configure VectorShift to output JSON matching these schemas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="resume-schema">
            <TabsList className="grid w-full max-w-2xl grid-cols-4">
              <TabsTrigger value="resume-schema">Resume</TabsTrigger>
              <TabsTrigger value="letter-schema">Cover Letter</TabsTrigger>
              <TabsTrigger value="linkedin-schema">LinkedIn</TabsTrigger>
              <TabsTrigger value="bio-schema">Exec Bio</TabsTrigger>
            </TabsList>
            <TabsContent value="resume-schema" className="mt-4">
              <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-[300px]">
{`{
  "Name": "string",
  "Phone": "string",
  "Email": "string",
  "City": "string",
  "State": "string",
  "LinkedIn": "string",
  "Summary": "string",
  "Skills": [{ "Skill": "string" }],
  "CareerHighlights": [{ "chtitle": "string", "chdescription": "string" }],
  "ProfessionalExperience": [{
    "job_title": "string",
    "company": "string",
    "location": "string",
    "start_date": "string",
    "end_date": "string",
    "Descriptions": [{ "Description": "string" }]
  }],
  "Education": [{
    "College_Degree_Name": "string",
    "Major": "string",
    "College_Name": "string",
    "City": "string",
    "ST": "string",
    "Year_Earned": "string"
  }],
  "CertificationsAndLicenses": [],
  "TechnicalSkills": [],
  "Languages": [],
  "AwardsAndHonors": [],
  "ProfessionalAffiliations": [],
  "Publications": [],
  "Projects": [],
  "Presentations": [],
  "Portfolio": [],
  "TrainingAndDevelopment": [],
  "MilitaryExperience": [],
  "VolunteerMemberships": []
}`}
              </pre>
            </TabsContent>
            <TabsContent value="letter-schema" className="mt-4">
              <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-[300px]">
{`{
  "Name": "string",
  "Phone": "string",
  "Email": "string",
  "Address": "string",
  "LinkedIn": "string",
  "CompanyName": "string",
  "RecipientName": "string",
  "RecipientTitle": "string",
  "JobTitle": "string",
  "Body": "string (use \\n for line breaks)"
}`}
              </pre>
            </TabsContent>
            <TabsContent value="linkedin-schema" className="mt-4">
              <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-[300px]">
{`{
  "LinkedIn_First_Name": "string",
  "LinkedIn_Last_Name": "string",
  "LinkedIn_Headline_1": "string",
  "LinkedIn_Headline_2": "string",
  "LinkedIn_About": "string (use \\n for line breaks)",
  "LinkedIn_Experience": "string (use \\n for line breaks)",
  "LinkedIn_URL": "string"
}`}
              </pre>
            </TabsContent>
            <TabsContent value="bio-schema" className="mt-4">
              <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-[300px]">
{`{
  "Name": "string",
  "Title": "string",
  "Phone": "string",
  "Email": "string",
  "City": "string",
  "State": "string",
  "LinkedIn": "string",
  "BioParagraphs": ["string", "string", ...],
  "KeyAccomplishments": ["string", "string", ...],
  "Expertise": ["string", "string", ...]
}`}
              </pre>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
